import { produce } from 'immer';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import React, { useState, useEffect, useRef } from 'react';

import {
  CustomNotification,
  StyledModal,
  WithLoading,
  dialogFunction,
} from 'common/components';
import AplSendSampleRequestEditor from './AplSendSampleRequestEditor';
import AplSendSampleRequestTemplateList from './AplSendSampleRequestTemplateList';

import { EVENT } from 'static/Constants';

import * as aplServices from 'services/apl';
import { apiHandler } from 'utils/api';
import { sleep } from 'utils/delay';
import {
  APL_SAMPLE_REQUEST_TEMPLATE_LIST,
  EMAIL_CONTENT_SHORT_CODE,
  EMAIL_HTML_TEMPLATE,
} from './Constants';

import Messages from 'i18n/messages/apl';

import './AplSendSampleRequestModal.less';
import { getEmailTitle } from './utils';

const AplSendSampleRequestModal = (props) => {
  const intl = useIntl();
  const dataRef = useRef();
  const validateFormRef = useRef();
  const [emailForm] = Form.useForm();

  const { visible, setVisible, sampleDataByEmail } = props;

  const [template, setTemplate] = useState(null);
  const [templateList, setTemplateList] = useState([]);

  const [senderInfo, setEnderInfo] = useState();
  const [invalidTag, setInvalidTag] = useState([]);
  const [sendLoading, setSendLoading] = useState(false);
  const [requestSampleData, setRequestSampleData] = useState();
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [currentContact, setCurrentContact] = useState();

  const onCancel = () => {
    if (template) showCloseNotification();
    else closeModal();
  };

  const closeModal = () => {
    setRequestSampleData(null);
    setTemplate(null);
    setInvalidTag([]);
    setVisible(false);
    setSendLoading(false);
    setCurrentContact(null);
    dispatchClearSectionSelectionEvent();
  };

  const showCloseNotification = () => {
    dialogFunction({
      type: 'warn',
      content: intl.formatMessage(Messages.closeMessage),
      onOk: closeModal,
    });
  };

  const getEmailContent = () => {
    const html = dataRef.current?.getHtml();

    const emailContent = EMAIL_HTML_TEMPLATE.replace(
      EMAIL_CONTENT_SHORT_CODE,
      html
    );
    return emailContent;
  };

  const onChangeContactEmail = async (contactEmail) => {
    const { emailTitle, email } = await emailForm.getFieldsValue();

    setLoadingTemplate(true);

    const emailContent = getEmailContent();

    const _saveData = {
      ...requestSampleData,
      [currentContact]: {
        ...requestSampleData[currentContact],
        emailContent,
        currentContact,
        emailTitle,
        email,
      },
    };

    const subject = getEmailTitle({
      sampleDataByEmail,
      contactEmail: contactEmail,
    });

    const emptyEmail =
      contactEmail === 'Empty' && !requestSampleData[contactEmail]?.email
        ? ''
        : contactEmail;

    emailForm.setFieldsValue({
      emailTitle:
        requestSampleData[contactEmail]?.emailTitle != null
          ? requestSampleData[contactEmail]?.emailTitle
          : subject,
      email: requestSampleData[contactEmail]?.email || emptyEmail,
    });
    setRequestSampleData(_saveData);
    setCurrentContact(contactEmail);
    await sleep(300);
    setLoadingTemplate(false);
  };

  const getSenderInfo = () => {
    if (!visible) return;

    aplServices.getSampleRequestSenderData().then((response) => {
      const { isSuccess, data } = response;
      if (isSuccess && data) setEnderInfo(data);
    });
  };

  const handleValidateAllEmail = (allValues) => {
    const isValid = Object.keys(allValues)?.every((requestSample) => {
      return (
        requestSampleData[requestSample]?.isValid &&
        requestSampleData[requestSample]?.isValidEmail
      );
    });

    if (!isValid) {
      CustomNotification.warning(
        'Please ensure all fields are completed in the email.'
      );
    }

    return isValid;
  };

  const callApiSendSampleRequest = async () => {
    validateFormRef.current?.validateForm();
    const { email, emailTitle } = await emailForm.validateFields();

    const emailContent = getEmailContent();

    const allValues = {
      ...requestSampleData,
      [currentContact]: {
        emailContent,
        emailTitle,
        currentContact,
        email,
      },
    };

    const isValidAllEmail = handleValidateAllEmail(allValues);

    if (!email || !isValidAllEmail) return;

    setSendLoading(true);

    const mailContents = Object.keys(allValues)?.map((requestSample) => {
      const { emailContent, emailTitle, email } = allValues[requestSample];
      return {
        email,
        emailTitle,
        emailContent,
      };
    });

    const params = {
      mailContents,
    };

    const successMessage = intl.formatMessage(
      Messages.sendSampleRequestSuccess
    );
    const errorMessage = intl.formatMessage(Messages.sendSampleRequestError);

    apiHandler({
      service: aplServices.sendSampleRequest,
      params,
      successMessage,
      errorMessage,
      onFinally: onSendFinally,
    });
  };

  const onSendFinally = () => {
    closeModal();
  };

  const getTemplateList = () => {
    setTemplateList(APL_SAMPLE_REQUEST_TEMPLATE_LIST);
  };

  const initEmailForm = () => {
    const firstContactEmail = Object.keys(sampleDataByEmail)?.[0];

    const currentEmail = firstContactEmail === 'Empty' ? '' : firstContactEmail;

    const subject = getEmailTitle({
      sampleDataByEmail,
      contactEmail: firstContactEmail,
    });

    emailForm.setFieldsValue({
      emailTitle: subject,
      email: currentEmail,
    });
  };

  const initState = () => {
    setRequestSampleData(sampleDataByEmail);
    setCurrentContact(Object.keys(sampleDataByEmail)?.[0]);
  };

  const init = () => {
    if (!visible && !sampleDataByEmail) return;

    initEmailForm();
    initState();
  };

  const dispatchClearSectionSelectionEvent = () => {
    const event = new Event(EVENT.EMAIL_EDITOR_CLEAR_SELECTED_SECTION);

    document.dispatchEvent(event);
  };

  useEffect(() => {
    getSenderInfo();
    getTemplateList();
    init();
  }, [visible]);

  return (
    <StyledModal
      title={intl.formatMessage(Messages.requestSample)}
      width={'85vw'}
      bodyStyle={{ height: '80vh' }}
      visible={visible}
      maskClosable={false}
      onCancel={onCancel}
      onOk={callApiSendSampleRequest}
      okText={intl.formatMessage(Messages.send)}
      okButtonProps={{ loading: sendLoading, disabled: !template }}
    >
      <WithLoading loading={loadingTemplate}>
        {template ? (
          <AplSendSampleRequestEditor
            dataRef={dataRef}
            invalidTag={invalidTag}
            setInvalidTag={setInvalidTag}
            validateFormRef={validateFormRef}
            requestSampleData={requestSampleData}
            setRequestSampleData={setRequestSampleData}
            currentContact={currentContact}
            onChangeContactEmail={onChangeContactEmail}
            emailForm={emailForm}
            template={template}
            senderInfo={senderInfo}
          />
        ) : (
          <AplSendSampleRequestTemplateList
            templateList={templateList}
            setTemplate={setTemplate}
          />
        )}
      </WithLoading>
    </StyledModal>
  );
};

export default AplSendSampleRequestModal;
